import type { Map } from 'immutable';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';

import { EPOCH_ISO } from '../../utils/date';
import type { Account } from '../accounts/accountsTypes';
import { chartOfAccountsTypes } from '../chart-of-accounts';
import { transactionsTypes } from '../transactions';
import type { Goal, GoalRecord } from './goalsTypes';

const FORMAT_LLLL_yyyy = 'LLLL yyyy';

type looseSubtype = 'GOAL_SPENT' | null;

type GoalTxns = Map<string, transactionsTypes.TransactionRecord> | undefined;

type LooseGoal = Partial<GoalRecord | Goal>;

const todayString = DateTime.now().toISODate();

export const makeGoalTransactions = (
  goal: Goal,
  fundingAccountId: string,
  amount: number,
  subtype?: looseSubtype,
  date?: string | null,
) => {
  const clientId1 = uuidv4();
  const clientId2 = uuidv4();
  const fromTxn = makeGoalTransferTxn(
    goal,
    fundingAccountId,
    goal.accountId,
    -1 * amount,
    clientId1,
    clientId2,
    null,
    date,
  );
  const toTxn = makeGoalTransferTxn(
    goal,
    goal.accountId,
    fundingAccountId,
    amount,
    clientId2,
    clientId1,
    subtype,
    date,
  ); // subtype only applied on goal-account side
  return [fromTxn, toTxn];
};

// currently only used in mobile because lacks QData/preprocess and meta context
export const createTransfer = (
  goal: Goal,
  fundingAccountId: string,
  amount: number,
  subtype?: looseSubtype,
  date?: string | null,
) => {
  const txns = makeGoalTransactions(goal, fundingAccountId, amount, subtype, date);
  const context = amount < 0 ? 'WithdrawFromGoal' : 'ContributeToGoal';
  return { txnPack: txns, context };
};

export const makeGoalTransferTxn = (
  goal: Goal,
  fromAccountId: string | undefined,
  toAccountId: string | undefined,
  amount: number,
  fromClientId?: string,
  toClientId?: string,
  subtype?: looseSubtype,
  date?: string | null,
) =>
  transactionsTypes.mkCashFlowTransaction({
    accountId: fromAccountId,
    payee: `${goal.name} Goal`,
    coa: chartOfAccountsTypes.mkChartOfAccount({
      id: toAccountId,
      type: chartOfAccountsTypes.CoaTypeEnum.ACCOUNT,
    }),
    clientId: toClientId,
    type: 'CASH_FLOW',
    subtype: subtype as unknown as transactionsTypes.TransactionSubType,
    tags: goal.tagId ? [{ id: goal.tagId }] : undefined, // todo: maybe use retriever to put full tag here?
    isExcludedFromF2S: true,
    isExcludedFromReports: true,
    matchState: 'USER_UNMATCHED',
    state: 'CLEARED',
    transfer: { clientId: fromClientId },
    amount,
    postedOn: date || DateTime.now().toISODate(),
  });

export const getGoalAccountBalance = (goal: Goal, goalAccount?: Account) =>
  goal?.savedSoFar || goalAccount?.currentBalanceAsOf || 0;

export const getAverageContribution = (goal: Goal, goalTxnsStartingMonth: GoalTxns) => {
  const startingContribution =
    goalTxnsStartingMonth && goalTxnsStartingMonth.size > 0
      ? goalTxnsStartingMonth
          .map((txn) => (txn.subtype === 'GOAL_STARTING_AMOUNT' ? Number(txn.amount) : 0))
          .reduce((sum, amt) => sum + amt, 0)
      : 0;
  const goalStartDate = DateTime.fromISO(goal.startOn);
  const monthsDiff = Math.ceil(DateTime.local().diff(goalStartDate, 'months').toObject().months || 0);
  const safeMonthsDiff = monthsDiff > 1 ? monthsDiff : 1;
  return goal.savedSoFar ? (goal.savedSoFar - startingContribution) / safeMonthsDiff : 0;
};

export const getMonthlyContribution = (goalTxnsForCurrentMonth: GoalTxns) =>
  goalTxnsForCurrentMonth && goalTxnsForCurrentMonth.size > 0
    ? goalTxnsForCurrentMonth
        .map((txn) => (txn.subtype !== 'GOAL_SPENT' && txn.subtype !== 'GOAL_STARTING_AMOUNT' ? Number(txn.amount) : 0))
        .reduce((sum, amt) => sum + amt, 0)
    : 0;

export const formatGoalDates = (goal: LooseGoal) => ({
  targetOn:
    goal.targetOn && goal.targetOn !== EPOCH_ISO ? DateTime.fromISO(goal.targetOn).toFormat(FORMAT_LLLL_yyyy) : null,
  startOn: DateTime.fromISO(goal.startOn || todayString).toFormat(FORMAT_LLLL_yyyy),
  completedOn: goal.completedOn ? DateTime.fromISO(goal.completedOn).toFormat(FORMAT_LLLL_yyyy) : null,
});

export const isGoalCompleted = (goal: LooseGoal) => {
  const { completedOn } = formatGoalDates(goal);
  if (!goal.savedSoFar) return false;
  if (!goal.targetAmount) return true;
  if (completedOn && completedOn.length > 0 && completedOn !== EPOCH_ISO) {
    if (goal.savedSoFar < goal.targetAmount) {
      return false;
    }
    return true;
  }
  if (goal.savedSoFar >= goal.targetAmount) {
    return true;
  }
  return false;
};

export const getExpectedEndDate = (goal: LooseGoal, goalTxnsForCurrentMonth: GoalTxns, contribution = 0) => {
  if (isGoalCompleted(goal)) {
    return goal.completedOn ? DateTime.fromISO(goal.completedOn) : DateTime.now();
  }
  const currentContribution = goal.savedSoFar || 0;
  const targetMonthlyContribution = contribution || goal.contributionAmount || 1;
  const thisMonthsContribution = getMonthlyContribution(goalTxnsForCurrentMonth);
  const adjustmentForEndOfMonth = Math.max(targetMonthlyContribution - thisMonthsContribution, 0);
  const balanceAtEndOfThisMonth = currentContribution + adjustmentForEndOfMonth;

  const monthsLeft =
    Math.ceil(Math.max(Number(goal.targetAmount) - balanceAtEndOfThisMonth, 0) / targetMonthlyContribution) || 1;

  const today = DateTime.local().startOf('month');
  const goalStartOn = DateTime.fromISO(goal.startOn || todayString);
  let startDate = today > goalStartOn ? today : goalStartOn;
  // Include current month when there is no contribution. Assume user has until end of month if there is contribution. Set startDate to last day of the month.
  startDate = balanceAtEndOfThisMonth > 0 ? startDate.endOf('month') : startDate.minus({ days: 1 }); // -1 is to include start month also
  const endDate = startDate.plus({ months: monthsLeft });
  return endDate;
};

export const getTargetContribAmt = (goal: Goal) => (goal.contributionAmount ? goal.contributionAmount : 0);

// formats month and year string to full DateTime object
export const getFullDate = (monthString: string | number, yearString: string | number) =>
  DateTime.fromFormat(`${monthString} ${yearString}`, 'MMM yyyy');

// Format amount string to float
export const parseAmount = (amount?: string | number) => {
  if (!amount) return 0;
  if (typeof amount === 'number') return amount;
  return Number.isNaN(Number.parseFloat(amount)) ? 0 : Number.parseFloat(amount);
};
